import {Duration} from '../Domain/Duration';
import {GeoTrack} from '../Models/GeoTrack';
import {errorResponse, NetworkRequestStatus, NetworkResult, successResponse} from './NetworkResult';
import {HeartRateTrack} from '../Models/HeartRateTrack';
import {HeartRate} from '../Domain/HeartRate';
import {StrokeRate} from '../Domain/StrokeRate';
import {GeoTrackStrokes} from '../Models/GeoTrackStrokes';

export class TracksDownloadService {
    public async forGeoTrackUrl(url: string | null): Promise<NetworkResult<GeoTrack>> {
        try {
            const networkResult = await this.forTrackUrl<
                Array<{
                    lat: number;
                    lng: number;
                    elapsed_time_millis: number;
                }>
            >(url);

            switch (networkResult.status) {
                case NetworkRequestStatus.Error:
                    return networkResult;
                case NetworkRequestStatus.Loading:
                    throw new Error('Unexpected network result');
                case NetworkRequestStatus.Success:
                    return successResponse(
                        networkResult.data.map((point) => {
                            return {
                                lat: point.lat,
                                lng: point.lng,
                                elapsed_time: Duration.fromMilliseconds(point.elapsed_time_millis),
                            };
                        }),
                    );
            }
        } catch (e) {
            return errorResponse();
        }
    }

    public async forGeoStrokesTrackUrl(url: string | null): Promise<NetworkResult<GeoTrackStrokes>> {
        try {
            const networkResult = await this.forTrackUrl<
                Array<{
                    elapsed_time_millis: number;
                    stroke_rate: number;
                }>
            >(url);

            switch (networkResult.status) {
                case NetworkRequestStatus.Error:
                    return networkResult;
                case NetworkRequestStatus.Loading:
                    throw new Error('Unexpected network result');
                case NetworkRequestStatus.Success:
                    return successResponse(
                        networkResult.data.map((point) => {
                            return {
                                elapsedTime: Duration.fromMilliseconds(point.elapsed_time_millis),
                                strokeRate: StrokeRate.fromStrokesPerMinute(point.stroke_rate),
                            };
                        }),
                    );
            }
        } catch (e) {
            return errorResponse();
        }
    }

    public async forHeartRateTrackUrl(url: string | null): Promise<NetworkResult<HeartRateTrack>> {
        try {
            const networkResult = await this.forTrackUrl<
                Array<{
                    bmp?: number; //There was a brief time where heart rate was stored as bmp instead of bpm...
                    bpm: number;
                    elapsed_time_millis: number;
                }>
            >(url);

            switch (networkResult.status) {
                case NetworkRequestStatus.Error:
                    return networkResult;
                case NetworkRequestStatus.Loading:
                    throw new Error('Unexpected network result');
                case NetworkRequestStatus.Success:
                    return successResponse(
                        networkResult.data.map((point) => {
                            return {
                                heart_rate: point.bmp ? HeartRate.fromBpm(point.bmp) : HeartRate.fromBpm(point.bpm),
                                elapsed_time: Duration.fromMilliseconds(point.elapsed_time_millis),
                            };
                        }),
                    );
            }
        } catch (e) {
            return errorResponse();
        }
    }

    private async forTrackUrl<T>(url: string | null): Promise<NetworkResult<T>> {
        if (url === null) {
            return errorResponse();
        }

        try {
            const response = await fetch(url);

            return successResponse(await response.json());
        } catch (e) {
            return errorResponse();
        }
    }
}
